import React, { useState } from "react";
import { useCreateSource } from "../../hooks/useApi";
import { useTranslation } from "../../hooks/useTranslation";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import { Loader2, AlertTriangle, Info } from "lucide-react";

const SitemapSourceDialog = ({ isOpen, onClose, botId, onSuccess }) => {
  const { t } = useTranslation();
  const [url, setUrl] = useState("");
  const [refreshDays, setRefreshDays] = useState(5);
  const [error, setError] = useState(null);

  const createSourceMutation = useCreateSource();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    if (!url.trim()) {
      setError(t("dialogs.sitemap.errorEnterUrl", "Please enter a sitemap URL"));
      return;
    }

    // Basic URL validation
    try {
      new URL(url);
    } catch {
      setError(t("dialogs.sitemap.errorInvalidUrl", "Please enter a valid URL"));
      return;
    }

    // Basic sitemap URL validation
    const lowerUrl = url.toLowerCase();
    if (!lowerUrl.includes("sitemap") && !lowerUrl.endsWith(".xml")) {
      setError(
        t(
          "dialogs.sitemap.errorNotSitemap",
          "URL should be a valid sitemap (contain 'sitemap' or end with '.xml')"
        )
      );
      return;
    }

    try {
      await createSourceMutation.mutateAsync({
        botId,
        data: {
          type: "sitemap",
          url: url.trim(),
          refresh_days: refreshDays,
        },
      });

      // Success
      setUrl("");
      setRefreshDays(5);
      setError(null);
      onSuccess?.();
      onClose();
    } catch (err) {
      console.error("Error creating sitemap source:", err);
      setError(
        err.response?.data?.message ||
          err.message ||
          t("dialogs.sitemap.errorCreateFailed", "Failed to create sitemap source")
      );
    }
  };

  const handleClose = () => {
    setUrl("");
    setRefreshDays(5);
    setError(null);
    onClose();
  };

  const isLoading = createSourceMutation.isPending;

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>
            {t("dialogs.sitemap.title", "Add Sitemap Source")}
          </DialogTitle>
          <DialogDescription>
            {t(
              "dialogs.sitemap.description",
              "Add a sitemap URL to automatically import all URLs from the sitemap as individual URL sources."
            )}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Info Alert */}
          <div className="flex items-start p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <Info className="h-4 w-4 text-blue-600 mr-3 mt-0.5 flex-shrink-0" />
            <p className="text-sm text-blue-800">
              {t(
                "dialogs.sitemap.info",
                "The sitemap will be parsed and all URLs will be added as individual URL sources. Nested sitemaps are supported, but other sitemaps won't be added as sources."
              )}
            </p>
          </div>

          {/* URL Input */}
          <div className="space-y-2">
            <Label htmlFor="sitemap-url">
              {t("dialogs.sitemap.urlLabel", "Sitemap URL *")}
            </Label>
            <Input
              id="sitemap-url"
              type="url"
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              placeholder="https://example.com/sitemap.xml"
              disabled={isLoading}
              className="w-full"
            />
            <p className="text-sm text-muted-foreground">
              {t(
                "dialogs.sitemap.urlHelp",
                "Enter the URL of the sitemap to parse. Should contain 'sitemap' or end with '.xml'."
              )}
            </p>
          </div>

          {/* Refresh Frequency */}
          <div className="space-y-2">
            <Label htmlFor="refresh-days">
              {t(
                "dialogs.crawl.frequencyLabel",
                "Frequenza di aggiornamento automatico"
              )}
            </Label>
            <Select
              value={refreshDays.toString()}
              onValueChange={(value) => setRefreshDays(parseInt(value))}
              disabled={isLoading}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="5">
                  {t("dialogs.crawl.every5", "Ogni 5 giorni")}
                </SelectItem>
                <SelectItem value="10">
                  {t("dialogs.crawl.every10", "Ogni 10 giorni")}
                </SelectItem>
                <SelectItem value="30">
                  {t("dialogs.crawl.every30", "Ogni 30 giorni (consigliato)")}
                </SelectItem>
              </SelectContent>
            </Select>
            <p className="text-sm text-muted-foreground">
              {t(
                "dialogs.crawl.helpSitemap",
                "Il bot ricontrollerà la sitemap con questa frequenza e aggiornerà la conoscenza se il contenuto cambia. Tutti gli URL della sitemap erediteranno questa frequenza."
              )}
            </p>
          </div>

          {/* Error Display */}
          {error && (
            <div className="flex items-start p-4 bg-red-50 border border-red-200 rounded-lg">
              <AlertTriangle className="h-4 w-4 text-red-600 mr-3 mt-0.5 flex-shrink-0" />
              <p className="text-sm text-red-800">{error}</p>
            </div>
          )}

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={handleClose}
              disabled={isLoading}
            >
              {t("common.cancel", "Cancel")}
            </Button>
            <Button type="submit" disabled={isLoading}>
              {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {isLoading
                ? t("dialogs.sitemap.processing", "Processing...")
                : t("dialogs.sitemap.submit", "Add Sitemap")}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default SitemapSourceDialog;
